/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */

import './DashboardTable.scss';
import classnames from 'classnames';
import { camelCase, isNil, isString } from 'lodash';
import {
  array,
  shape,
  bool,
  string,
  oneOfType,
  arrayOf,
  object,
  number,
  element
} from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { ReactComponent as ArrowUp } from 'assets/icons/market-up.svg';
import { ReactComponent as ArrowDown } from 'assets/icons/market-down.svg';
import AccountName from 'components/shared/Table/AccountName';
import CarrotToggle from 'components/shared/Table/CarrotToggle';
import CashHoldingsRow from 'components/shared/Table/CashHoldingsRow';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import {
  Dollar,
  EvaluatedDollar,
  EvaluatedPercent,
  FormattedDate
} from 'components/shared/Text/Text';
import Text from 'components/shared/Text/Text';
import {
  productMapByNumber,
  isPershingProduct,
  isBrokerage,
  isActivePlus
} from 'services/Products';
import { isPositiveValue, isNegativeValue } from 'services/Calculation';
import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';
import Content, { EmptyLine } from 'services/Content';
import { activePlusAccountUrl } from 'services/Externals';
import Toggle from 'components/shared/ToggleRP';
import { metaDataTypes, tableTypes } from 'services/TableSupport';
import Optioner from 'components/shared/Optioner/Optioner';
import IntradayRefresh from 'components/shared/Intraday/IntradayRefresh';
import { getAccountOverviewTableDataWithMmr } from 'state/account/AccountOverviewTableSelectors';
import {
  getCustomerWebView,
  getUberId
} from 'state/customer/CustomerSelectors';
import IntradayAsOfDate from 'components/shared/Intraday/IntradayAsOfDate';
import { getIntradayRefreshEnabled } from 'state/account/AccountSelectors';
import AccountLabel from 'components/shared/AccountLabel/AccountLabel';
import { intradayRefreshEnabled } from 'services/FeatureFlags';
import { intradayEligibleFilter } from 'services/Accounts';

export const isHoldingRow = (row) => {
  return row._type === tableTypes.HOLDING_ROW;
};
// Test if an APP transaction is pending.
export const isPending = (props) => {
  return (
    !props.name.isClosed &&
    isActivePlus(props.name.productType) &&
    props.marketValue === 0
  );
};
const defaultColumns = [
  'Account',
  'Refresh',
  'Balance',
  'Daily Change',
  'Since Inception'
];

/**
 * Allows for someone to pass in a string and use the default header or
 * pass in their own specific header.
 *
 * @param columns
 * @param hideHeaders
 * @returns {*}
 */
export const ColumnList = ({ columns, hideHeaders }) => {
  const headers = columns.map((column) => {
    // special case for the (prr) since inception column
    if (column === 'Since Inception') {
      const disclosure = (
        <DisclosureModal
          data-testid={'restricted-height-modal'}
          className="restricted-height-modal"
          content={Content.prrPerformanceDisclosure}
          hideLinkContent
        />
      );
      return (
        <th
          data-testid="prr-column"
          className={`e2e-${camelCase(column)} prr-column`}
          key={column}>
          PRR Since Inception {disclosure}
        </th>
      );
    }

    if (column === 'Refresh') {
      return (
        <th
          data-testid={'refresh-column'}
          className={`e2e-${camelCase(column)} refresh-column`}
          key={column}
        />
      );
    }

    return isString(column) ? (
      <th
        data-testid={`column-${camelCase(column)}`}
        className={`e2e-${camelCase(column)}`}
        key={column}>
        {column}
      </th>
    ) : (
      column.component
    );
  });

  const columnClassName = classnames('columns', {
    'hide-headers': hideHeaders
  });

  return <tr className={columnClassName}>{headers}</tr>;
};

ColumnList.propTypes = {
  columns: arrayOf(oneOfType([string, element]))
};

export const ValueIndicator = ({ value }) => (
  <Optioner
    val={{
      positive: isPositiveValue(value),
      negative: isNegativeValue(value)
    }}>
    {{
      positive: <ArrowUp className="value-arrow-up" />,
      negative: <ArrowDown className="value-arrow-down" />,
      _: <span className="no-value" />
    }}
  </Optioner>
);

const GroupLabelOwners = ({ owners }) => (
  <div className="group-owner-names e2e-group-owner-names">{owners}</div>
);

const GroupLabelOwnersWithPermissions = WithUserPermissions(GroupLabelOwners);

const TableGroupName = ({ metaData, name }) => {
  return (
    <td className="account-name-group">
      <div className="parent-account-type-label">
        <div>{name.groupLabel.accountType || ''}</div>
        {metaData.ownerNames === metaDataTypes.SHOW && (
          <GroupLabelOwnersWithPermissions
            owners={name.groupLabel.owners}
            shouldMask
          />
        )}
      </div>
    </td>
  );
};

TableGroupName.propTypes = {
  name: shape({
    groupLabel: oneOfType([string, object])
  })
};

const AccountDetailsInfo = ({
  accountWChecksum,
  detailsMetadata,
  metaDataTypes,
  ownerNames
}) => (
  <div className="account-details">
    <span className="account-id e2e-account-id">{accountWChecksum}</span>
    {detailsMetadata.ownerNames === metaDataTypes.SHOW && ownerNames && (
      <span className="spacing-dot" />
    )}
    <span className="account-owners e2e-account-owners">
      {detailsMetadata.ownerNames === metaDataTypes.SHOW &&
        ownerNames &&
        ownerNames}
    </span>
  </div>
);

const AccountNameWithPermissions = WithUserPermissions(AccountName);
const AccountDetailsInfoWithPermissions =
  WithUserPermissions(AccountDetailsInfo);

export const AccountDetail = ({
  asOfDate,
  accountWChecksum,
  brokerageAccountId,
  cusip,
  accountId,
  ownerNames,
  ticker,
  productType,
  detailsMetadata,
  isHoldingRow,
  toggleHoldingsView,
  viewHoldings,
  showIntradayRefresh,
  ...accountNameProps
}) => {
  return (
    <td className="account-name-group">
      <div className="brokerage-carrot-group">
        {toggleHoldingsView && (
          <CarrotToggle
            toggleChildrenView={toggleHoldingsView}
            viewChildren={viewHoldings}
          />
        )}
      </div>
      <div className="account-info-group">
        <div className="account-name">
          {detailsMetadata.accountLink === metaDataTypes.HIDE ? (
            accountNameProps.accountName
          ) : isHoldingRow ? (
            <Link
              className="app-link link-text"
              to={`/account-details?id=${brokerageAccountId}&investment=${cusip}`}>
              {accountNameProps.accountName}{'-'}{ticker}

            </Link>
          ) : (
            <AccountNameWithPermissions
              productType={productType}
              accountId={accountId}
              shouldDisable={isBrokerage(productType)}
              styleOverride={{ display: 'inline-flex' }}
              shouldMask
              {...accountNameProps}
            />
          )}
          {intradayRefreshEnabled() &&
            detailsMetadata.asOfDate === metaDataTypes.SHOW && (
              <IntradayAsOfDate
                productType={productType}
                accountId={accountId}
                asOfDate={asOfDate}
              />
            )}
          {detailsMetadata.cusip !== metaDataTypes.HIDE && (
            <span className="account-ticker e2e-holding-cusip">{cusip}</span>
          )}
          {detailsMetadata.ticker !== metaDataTypes.HIDE &&
            (detailsMetadata.ticker === metaDataTypes.STATIC ? (
              <span className="account-ticker e2e-account-ticker">
                {ticker}
              </span>
            ) : (
              <Link
                className="app-link"
                to={`/account-details?id=${accountId}`}>
                <span className="account-ticker e2e-account-ticker">
                  {ticker}
                </span>
              </Link>
            ))}
        </div>
        <AccountDetailsInfoWithPermissions
          accountWChecksum={accountWChecksum}
          detailsMetadata={detailsMetadata}
          metaDataTypes={metaDataTypes}
          ownerNames={ownerNames}
          shouldMask
        />
        {!isHoldingRow && isBrokerage(productType) && showIntradayRefresh && (
          <IntradayRefresh
            hideIcon
            containerClassName="intraday-refreshing-label"
          />
        )}
        <AccountLabel accountId={accountId} />
      </div>
    </td>
  );
};

AccountDetail.propTypes = {
  accountName: string, // test data has a user with no name causing .isRequired here to error
  accountWChecksum: string,
  ownerNames: string,
  asOfDate: string,
  ticker: string
};

const Refresh = () => (
  <td className="account-refresh">
    <IntradayRefresh hideLabel />
  </td>
);

const Balance = ({ dollarAmount }) => (
  <Dollar className="account-balance" element="td">
    {dollarAmount}
  </Dollar>
);

Balance.propTypes = {
  dollarAmount: number
};

const DailyChange = ({ difference, percentChange }) => (
  <td className="daily-change">
    <span>
      <span>
        <EvaluatedDollar element="div">{difference}</EvaluatedDollar>
        <EvaluatedPercent className="e2e-daily-change-percent" element="div">
          {percentChange}
        </EvaluatedPercent>
      </span>
      <ValueIndicator value={difference} />
    </span>
  </td>
);

DailyChange.propTypes = {
  difference: number,
  percentChange: number
};

const Performance = ({
  percentReturn,
  inceptionDate,
  productType,
  accountId,
  isCWV,
  uberId
}) => (
  <td className="performance">
    {!isNil(percentReturn) && inceptionDate ? (
      <span>
        <EvaluatedPercent element="div">{percentReturn}</EvaluatedPercent>
        <FormattedDate className="inception-date">
          {inceptionDate}
        </FormattedDate>
      </span>
    ) : isActivePlus(productType) ? (
      <a
        href={activePlusAccountUrl(accountId, isCWV, uberId)}
        className="prr-link">
        View Return
      </a>
    ) : (
      <EmptyLine />
    )}
  </td>
);

Performance.propTypes = {
  percentReturn: number,
  inceptionDate: string
};

const EmptyCell = ({ className = '' }) => {
  const classNames = `${className}`;
  return <td className={classNames} />;
}; // For the columns in a parent row that shouldn't show data

const cellComponentMapper = (
  columnName,
  props,
  isParent,
  key,
  toggleHoldingsView,
  showIntradayRefresh,
  viewHoldings,
  row
) => {
  const mapper = {
    account:
      row._type === tableTypes.HEADER_ROW ? (
        <TableGroupName name={props.name} metaData={row.metaData} key={key} />
      ) : (
        <AccountDetail
          {...props.name}
          brokerageAccountId={props.brokerageAccountId}
          isViewOnly={props.isViewOnly}
          key={key}
          toggleHoldingsView={toggleHoldingsView}
          detailsMetadata={row.metaData}
          isHoldingRow={isHoldingRow(row)}
          isClosed={props.isClosed}
          viewHoldings={viewHoldings}
        />
      ),
    refresh:
      !isHoldingRow(row) &&
      intradayEligibleFilter(row.details) &&
      showIntradayRefresh ? (
        <Refresh key={key} />
      ) : (
        <EmptyCell key={key} />
      ),
    balance: isPending(props) ? (
      <Text className="account-balance" element="td">
        Transaction Pending
      </Text>
    ) : (
      <Balance dollarAmount={props.marketValue} key={key} />
    ),
    dailyChange: <DailyChange {...props.dailyChange} key={key} />,
    sinceInception: isParent ? (
      <EmptyCell key={key} />
    ) : (
      <Performance
        {...props.performance}
        productType={props.name.productType}
        accountId={props.name.accountId}
        key={key}
      />
    )
  };

  return mapper[columnName] || <td />;
};

const RowCells = ({
  rowData,
  columns,
  showIntradayRefresh,
  toggleHoldingsView,
  viewHoldings
}) =>
  columns.map((colName, key) => {
    const col = colName === '' ? 'name' : camelCase(colName);
    const isParent = rowData._type === tableTypes.HEADER_ROW;

    return cellComponentMapper(
      col,
      rowData.details,
      isParent,
      key,
      toggleHoldingsView,
      showIntradayRefresh,
      viewHoldings,
      rowData
    );
  });

RowCells.propTypes = {
  rowData: shape({
    details: object.isRequired,
    children: array
  }),
  columns: arrayOf(oneOfType([string, element]))
};

const Row = ({ rowData = {}, showIntradayRefresh, view }) => {
  const { hasAlaska529Only, hasMaryland529Only } = view;

  const { productType, name } = rowData.details;

  const isHeader = rowData._type === tableTypes.HEADER_ROW;
  const hasMmrMessage = !isNil(rowData.mmrMessage);

  const className = classnames({
    'row-parent': isHeader,
    [productMapByNumber[productType]]:
      isHeader && (hasAlaska529Only || hasMaryland529Only),
    [name.groupingClassName]: isHeader,
    'row-child': !isHeader,
    'next-row-is-mmr': hasMmrMessage
  });

  const mmrMessageHtml = `${rowData.mmrMessage} For questions, please call T. Rowe Price at 877-218-5162.`;

  return !isPershingProduct(rowData.details.productType) ||
    rowData.children.length === 0 ? (
    <Fragment>
      <tr className={className}>
        <RowCells
          rowData={rowData}
          showIntradayRefresh={showIntradayRefresh}
          columns={defaultColumns}
        />
      </tr>
      {hasMmrMessage && (
        <tr className="mmr-message-row">
          <td
            colSpan={4}
            dangerouslySetInnerHTML={{ __html: mmrMessageHtml }}></td>
        </tr>
      )}
    </Fragment>
  ) : (
    <Toggle>
      {({ on, toggle }) => (
        <Fragment>
          <tr className={className}>
            <RowCells
              rowData={rowData}
              columns={defaultColumns}
              showIntradayRefresh={showIntradayRefresh}
              viewHoldings={on}
              toggleHoldingsView={toggle}
            />
          </tr>
          {on &&
            rowData.children.map(({ details, metaData, _type }, idx) => {
              return details.isCashRow ? (
                <CashHoldingsRow
                  className="row-holding-child"
                  {...details}
                  key="cash">
                  <Performance />
                </CashHoldingsRow>
              ) : (
                <tr key={idx} className="row-holding-child">
                  <RowCells
                    rowData={{
                      _type,
                      metaData,
                      details: {
                        brokerageAccountId: rowData?.details?.name?.accountId,
                        ...details
                      }
                    }} // This table has an "account" column instead of "name", they work the same just a different key
                    showIntradayRefresh={showIntradayRefresh}
                    columns={defaultColumns}
                  />
                </tr>
              );
            })}
        </Fragment>
      )}
    </Toggle>
  );
};

RowCells.propTypes = {
  rowData: shape({
    details: object.isRequired,
    children: array
  })
};

export const combineRows = (rows, parentKey, showIntradayRefresh, view) => {
  const providedRows = rows ? rows : [];
  return [].concat(providedRows).reduce((acc, row, childKey) => {
    const key = parentKey ? parentKey : '';
    const combinedKey = `${key}:${childKey}`;

    return acc
      .concat(
        <Row
          rowData={row}
          showIntradayRefresh={showIntradayRefresh}
          view={view}
          key={combinedKey}
        />
      )
      .concat(
        row._type === tableTypes.ACCOUNT_ROW
          ? []
          : combineRows(row.children, combinedKey, showIntradayRefresh, view)
      );
  }, []);
};

const TableRows = ({ tableData, showIntradayRefresh, view }) =>
  combineRows(tableData, '', showIntradayRefresh, view);

TableRows.propTypes = {
  tableData: shape({
    details: object.isRequired,
    children: array
  })
};

const DashboardTable = ({ groups, showIntradayRefresh, view }) => {
  return (
    <div className="accounts-table-container">
      {groups.map((group, i) => (
        <table key={i} className="table-component accounts-overview-table">
          <thead>
            <ColumnList columns={defaultColumns} hideHeaders={i !== 0} />
          </thead>
          <tbody>
            <TableRows
              tableData={group}
              showIntradayRefresh={showIntradayRefresh}
              view={view}
            />
          </tbody>
        </table>
      ))}
    </div>
  );
};

DashboardTable.propTypes = {
  accountOverviewTable: arrayOf(
    shape({
      details: shape({
        account: string.isRequired,
        retirementAccount: bool.isRequired
      }),
      children: arrayOf(
        shape({
          details: shape({
            account: shape({
              accountName: string,
              accountWChecksum: string,
              ownerNames: object,
              asOfDate: string,
              ticker: string
            }),
            marketValue: number.isRequired,
            dailyChange: shape({
              difference: number.isRequired,
              percentChange: number.isRequired
            }),
            performance: shape({
              percentReturn: number,
              inceptionDate: string
            })
          })
        })
      )
    })
  )
};

const mapStateToProps = (state) => {
  return {
    groups: getAccountOverviewTableDataWithMmr(state),
    showIntradayRefresh: getIntradayRefreshEnabled(state),
    view: state.view,
    isCWV: getCustomerWebView(state),
    uberId: getUberId(state)
  };
};

export default connect(mapStateToProps)(DashboardTable);
